
/* Mobile quick-add uses additive cart requests, independent of Dawn's bulk queue. */
(() => {
  if (window.mobileBulkActionsInitialized) return;
  window.mobileBulkActionsInitialized = true;

  const pendingLists = new WeakSet();
  const mobile = () => window.matchMedia('(max-width: 989px)').matches;
  const root = () => window.Shopify?.routes?.root || '/';

  function quantityFor(input) {
    const value = Number(input.value);
    const min = Number(input.dataset.min);
    const step = Number(input.dataset.step);
    const max = input.dataset.max === '' ? Infinity : Number(input.dataset.max);

    if (
      !/^\d+$/.test(input.value.trim()) ||
      !Number.isSafeInteger(value) ||
      value < min ||
      value > max ||
      value % step !== 0
    ) {
      throw new Error(
        `Choose a quantity of at least ${min}, in multiples of ${step}` +
        (max === Infinity ? '.' : `, up to ${max}.`)
      );
    }

    return value;
  }

  // Refresh cart drawer without replacing the shopping bag.
  function updateCartDrawer(sections) {
    const drawer = document.querySelector('cart-drawer');
    const html = sections?.['cart-drawer'];

    if (!drawer || !html) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const replacement = doc.querySelector('.drawer__inner');
    const current = drawer.querySelector('.drawer__inner');

    if (!replacement || !current) return;

    current.innerHTML = replacement.innerHTML;
    drawer.classList.remove('is-empty');
  }

  // Update only the header cart quantity badge.
  // Preserve the bag SVG, header link and event listeners.
  function updateCartBadge(cartData) {
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (!cartIcon) return;

    const count = Number(cartData?.item_count) || 0;
    let bubble = cartIcon.querySelector('.cart-count-bubble');

    if (count === 0) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      cartIcon.appendChild(bubble);
    }

    bubble.classList.remove('hidden');
    bubble.innerHTML = '';

    const visibleCount = document.createElement('span');
    visibleCount.textContent = String(count);

    const accessibleCount = document.createElement('span');
    accessibleCount.className = 'visually-hidden';
    accessibleCount.textContent = `${count} items in cart`;

    bubble.appendChild(visibleCount);
    bubble.appendChild(accessibleCount);
  }

  // Keep local fields out of inherited automatic bulk change handlers.
  document.addEventListener('change', (event) => {
    if (
      mobile() &&
      event.target.closest('[data-mobile-bulk-controls]')
    ) {
      event.stopPropagation();
    }
  }, true);

  // Allow Enter to submit the selected variant.
  document.addEventListener('keydown', (event) => {
    if (
      !mobile() ||
      !event.target.matches('[data-mobile-bulk-quantity]') ||
      event.key !== 'Enter'
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    event.target
      .closest('[data-mobile-bulk-controls]')
      ?.querySelector('[data-mobile-bulk-add]')
      ?.click();
  }, true);

  document.addEventListener('click', async (event) => {
    const button = event.target.closest(
      '[data-mobile-bulk-add], [data-mobile-bulk-step]'
    );

    // AMP BIS_trigger links retain their existing handlers.
    if (!button || !mobile()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const controls = button.closest('[data-mobile-bulk-controls]');
    if (!controls) return;

    const list = controls.closest('quick-order-list');
    const input = controls.querySelector('[data-mobile-bulk-quantity]');
    const message = controls.querySelector('[data-mobile-bulk-message]');

    if (!list || !input || pendingLists.has(list)) return;

    if (message) message.textContent = '';

    // Increment / decrement quantity.
    if (button.hasAttribute('data-mobile-bulk-step')) {
      const step = Number(input.dataset.step);
      const min = Number(input.dataset.min);
      const max = input.dataset.max === '' ? Infinity : Number(input.dataset.max);
      const current = Number(input.value);

      input.value = String(
        Math.max(
          min,
          Math.min(
            max,
            (Number.isFinite(current) ? current : min) +
              Number(button.dataset.mobileBulkStep) * step
          )
        )
      );

      return;
    }

    const plan = controls.querySelector('[data-mobile-bulk-plan]');
    let quantity;

    try {
      quantity = quantityFor(input);

      if (plan && !plan.value && plan.required) {
        throw new Error('Choose a purchase option.');
      }

      if (
        plan?.value &&
        plan.options &&
        !Array.from(plan.options).some(
          option => option.value === plan.value && !option.disabled
        )
      ) {
        throw new Error('Choose an available purchase option.');
      }
    } catch (error) {
      if (message) message.textContent = error.message;
      return;
    }

    // Avoid racing a pending native bulk update.
    if (
      list.hasPendingQuantityUpdate ||
      list.queue?.length > 0
    ) {
      if (message) {
        message.textContent =
          'Please wait for your cart update to finish, then try again.';
      }
      return;
    }

    pendingLists.add(list);

    const buttons = Array.from(
      list.querySelectorAll(
        '[data-mobile-bulk-add], [data-mobile-bulk-step]'
      )
    );

    buttons.forEach(item => {
      item.disabled = true;
    });

    button.setAttribute('aria-busy', 'true');

    let added = false;
    const variantId = controls.dataset.variantId;

    try {
      const item = {
        id: variantId,
        quantity
      };

      if (plan?.value) {
        item.selling_plan = plan.value;
      }

      // Add the selected variant through Shopify AJAX.
      const response = await fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [item],
          sections: [
            'cart-drawer',
            'cart-icon-bubble'
          ],
          sections_url:
            window.location.pathname + window.location.search
        })
      });

      const data = await response.json();

      if (!response.ok || data.status >= 400) {
        throw new Error(
          data.description ||
          data.message ||
          'Could not add this item.'
        );
      }

      added = true;

      // Refresh drawer contents without replacing the header icon.
      updateCartDrawer(data.sections);

      // Read the actual current cart quantity from Shopify.
      const cartResponse = await fetch(root() + 'cart.js', {
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!cartResponse.ok) {
        throw new Error('Could not refresh cart information.');
      }

      const cartData = await cartResponse.json();

      // Update only the header quantity badge.
      updateCartBadge(cartData);

      // Notify Shopify components of the cart update.
      if (
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined'
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: list.id,
          cartData
        });
      }

      // Refresh popup quantities and totals.
      if (typeof list.refresh === 'function') {
        await list.refresh();
      }

      const updatedControls = Array.from(
        list.querySelectorAll('[data-mobile-bulk-controls]')
      ).find(
        element => element.dataset.variantId === variantId
      );

      const updatedMessage =
        updatedControls?.querySelector('[data-mobile-bulk-message]') ||
        message;

      if (updatedMessage) {
        updatedMessage.textContent = 'Added to cart.';
      }

      updatedControls
        ?.querySelector('[data-mobile-bulk-add]')
        ?.focus({ preventScroll: true });

    } catch (error) {
      // Never suggest retrying an add that already succeeded.
      if (message) {
        message.textContent = added
          ? 'Added to cart. Reload the page to see updated totals.'
          : (
              error.message ||
              'Could not add this item. Please try again.'
            );
      }

      if (!added) {
        console.error('Mobile bulk add failed:', error);
      }

    } finally {
      pendingLists.delete(list);

      buttons.forEach(item => {
        if (item.isConnected) item.disabled = false;
      });

      button.removeAttribute('aria-busy');
    }
  }, true);
})();
